import HistoryIcon from "@mui/icons-material/History";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import AccountService from "../AccountService";

const DEFAULT_PAGE_SIZE = 20;

function normalizeHistoryResponse(raw) {
    if (!raw || typeof raw !== "object") {
        return {
            content: [],
            totalElements: 0,
            totalPages: 0,
            number: 0,
            size: DEFAULT_PAGE_SIZE,
        };
    }
    const merged =
        raw.page && typeof raw.page === "object"
            ? { content: raw.content, ...raw.page }
            : raw;
    return {
        content: Array.isArray(merged.content) ? merged.content : [],
        totalElements: merged.totalElements ?? 0,
        totalPages: merged.totalPages ?? 0,
        number: merged.number ?? 0,
        size: merged.size ?? DEFAULT_PAGE_SIZE,
    };
}

function formatDelta(delta) {
    if (delta === null || delta === undefined || Number.isNaN(Number(delta))) {
        return "—";
    }
    const n = Number(delta);
    if (n > 0) return `+${n.toLocaleString()}`;
    return n.toLocaleString();
}

/**
 * Admin dialog: GET /v1/assistant/credits/history (JWT auth; ApiService decrypts encrypted payloads).
 */
export default function AssistantCreditsHistoryDialog({ open, onClose, subjectUserId = null }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(DEFAULT_PAGE_SIZE);
    const [data, setData] = useState({
        content: [],
        totalElements: 0,
        totalPages: 0,
        number: 0,
        size: DEFAULT_PAGE_SIZE,
    });

    const fetchPage = useCallback(
        async (pageIndex) => {
            setLoading(true);
            setError(null);
            try {
                const raw = await AccountService.getAssistantCreditsHistory({
                    page: pageIndex,
                    pageSize,
                });
                const norm = normalizeHistoryResponse(raw);
                setData(norm);
                setPage(norm.number ?? pageIndex);
            } catch (err) {
                const msg = err?.message || "Failed to load credits history";
                setError(msg);
                Logger.error("Assistant credits history dialog:", err);
                setData({
                    content: [],
                    totalElements: 0,
                    totalPages: 0,
                    number: pageIndex,
                    size: pageSize,
                });
            } finally {
                setLoading(false);
            }
        },
        [pageSize]
    );

    useEffect(() => {
        if (!open) return;
        setPage(0);
        fetchPage(0);
    }, [open, fetchPage]);

    const handlePageChange = (_e, nextOneBased) => {
        const nextZero = Math.max(0, nextOneBased - 1);
        setPage(nextZero);
        fetchPage(nextZero);
    };

    const handleClose = () => {
        onClose();
    };

    const rows = data.content;
    const totalPages = Math.max(1, data.totalPages || 1);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth scroll="paper">
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HistoryIcon color="action" />
                Coins ledger
                {subjectUserId ? (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400, ml: 0.5 }}>
                        (user {subjectUserId})
                    </Typography>
                ) : null}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <code>GET /v1/assistant/credits/history</code> with <code>page</code> (0-based) and{" "}
                    <code>pageSize</code> (1–100). Newest rows first on the server. Encrypted responses are handled like
                    assistant chat history. This client sends only those two query params—ensure the API scopes rows to the
                    admin-viewed user if needed.
                </Typography>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {loading && rows.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "min(60vh, 480px)" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Created (UTC)</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell align="right">Delta</TableCell>
                                    <TableCell align="right">Balance after</TableCell>
                                    <TableCell>Note</TableCell>
                                    <TableCell>Reference</TableCell>
                                    <TableCell>Id</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 && !loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            <Typography variant="body2" color="text.secondary">
                                                No ledger rows.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => (
                                        <TableRow key={row.id ?? `${row.createdAt}-${row.reference}`} hover>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                {row.createdAt
                                                    ? UtilService.formatDate(row.createdAt) || String(row.createdAt)
                                                    : "—"}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{row.eventType ?? "—"}</TableCell>
                                            <TableCell align="right">{formatDelta(row.delta)}</TableCell>
                                            <TableCell align="right">
                                                {row.balanceAfter != null && !Number.isNaN(Number(row.balanceAfter))
                                                    ? Number(row.balanceAfter).toLocaleString()
                                                    : "—"}
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 200 }}>
                                                <Typography variant="body2" noWrap title={row.note ?? ""}>
                                                    {row.note ?? "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 160 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                                                    noWrap
                                                    title={row.reference ?? ""}
                                                >
                                                    {row.reference ?? "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 120 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                                                >
                                                    {row.id ?? "—"}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }} flexWrap="wrap" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                        {data.totalElements != null
                            ? `${data.totalElements} row(s) total · page ${(data.number ?? page) + 1} of ${data.totalPages || totalPages}`
                            : null}
                    </Typography>
                    {data.totalPages > 1 && (
                        <Pagination
                            count={data.totalPages}
                            page={(data.number ?? page) + 1}
                            onChange={handlePageChange}
                            color="primary"
                            disabled={loading}
                            showFirstButton
                            showLastButton
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
